import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import CheckInModal from "@/components/admin/CheckInModal";

const NAVY = "#002169";

const CLASSIFICATION_META = {
  AtRisk:    { label: "At-Risk",   color: "#b91c1c", bg: "#fee2e2", Icon: WarningAmberOutlinedIcon },
  Excellent: { label: "Excellent", color: "#166534", bg: "#dcfce7", Icon: EmojiEventsOutlinedIcon },
  Trial:     { label: "Trial",     color: "#92400e", bg: "#fef3c7", Icon: HourglassTopOutlinedIcon },
  Good:      { label: "Good",      color: "#1d4ed8", bg: "#dbeafe", Icon: CheckCircleOutlineIcon },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0].slice(2)}` : iso;
}

function StatCard({ label, value, sub }) {
  return (
    <Box sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid #e5e7eb",
      backgroundColor: "#f9fafb",
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
    }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: "text.secondary" }}>{sub}</Typography>}
    </Box>
  );
}

const ChemistAccountHealthTab = ({ chemistUUID, chemistName }) => {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);

  const load = useCallback(async () => {
    if (!chemistUUID) return;
    setLoading(true);
    try {
      const [health, notesRes] = await Promise.all([
        phappsRequest(API_PATHS.CHECK_INS_CHEMIST(chemistUUID), "GET"),
        phappsRequest(API_PATHS.CHECK_INS_CHEMIST_NOTES(chemistUUID), "GET").catch(() => ({ checkIns: [] })),
      ]);
      setData(health);
      setNotes(notesRes?.checkIns || []);
      setError(null);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load account health"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="textSecondary">Loading account health…</Typography>
      </Box>
    );
  }

  if (error) return <Alert severity="warning">{error}</Alert>;
  if (!data || !data.Classification) {
    return (
      <Typography color="textSecondary" sx={{ py: 4, textAlign: "center" }}>
        Not subscribed to Quda — no account health data available.
      </Typography>
    );
  }

  const meta = CLASSIFICATION_META[data.Classification] || CLASSIFICATION_META.Good;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Chip
          icon={<meta.Icon sx={{ color: `${meta.color} !important` }} />}
          label={meta.label}
          sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 800, fontSize: "0.85rem", height: 32, px: 0.5 }}
        />
        {data.ExtremelyAtRisk && (
          <Chip
            icon={<WarningAmberOutlinedIcon sx={{ color: "#fff !important" }} />}
            label="Extremely at-risk"
            sx={{ backgroundColor: "#b91c1c", color: "#fff", fontWeight: 800, fontSize: "0.78rem", height: 32 }}
          />
        )}
        {data.StopCheckIns && (
          <Chip
            icon={<BlockIcon sx={{ color: "#6b7280 !important" }} />}
            label="Check-ins stopped"
            sx={{ backgroundColor: "#f3f4f6", color: "#6b7280", fontWeight: 800, fontSize: "0.78rem", height: 32 }}
          />
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" }, gap: 1.5 }}>
        <StatCard label="Orders (30d)" value={data.Orders30d ?? 0} />
        <StatCard label="SMS (30d)" value={data.Sms30d ?? 0} />
        <StatCard label="Last check-in" value={fmtDate(data.LastCheckInDate)} />
        <StatCard
          label="Next check-in"
          value={data.StopCheckIns ? "Stopped" : fmtDate(data.NextCheckInDate)}
          sub={data.StopCheckIns ? undefined : data.CheckInDue ? "Due now" : "Scheduled"}
        />
        <StatCard label="Last order" value={fmtDate(data.LastOrderDate)} />
        <StatCard label="Last SMS" value={fmtDate(data.LastSmsDate)} />
      </Box>

      {data.Classification === "AtRisk" && (
        <Alert severity={data.ExtremelyAtRisk ? "error" : "warning"} sx={{ mt: 1 }}>
          {data.ExtremelyAtRisk
            ? "This account has remained At-Risk for 30+ days despite a check-in. Consider escalating."
            : "This account has had zero orders and zero SMS in the last 30 days. A check-in call is recommended."}
        </Alert>
      )}

      {/* ── Check-in history + logging ── */}
      <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2, mt: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: NAVY }}>
              Check-ins
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Log a call or visit and set when we should come back to this pharmacy.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => setCheckInOpen(true)}
            sx={{ backgroundColor: NAVY, textTransform: "none", fontWeight: 700, "&:hover": { backgroundColor: "#001a54" } }}
          >
            Check in
          </Button>
        </Box>

        {notes.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Manual check-ins
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.75 }}>
              {notes.map((n) => (
                <Box key={n.id} sx={{ borderLeft: `3px solid ${NAVY}`, pl: 1.5, py: 0.25 }}>
                  <Typography variant="caption" sx={{ color: "#6b7280" }}>
                    {fmtDate(n.check_in_at)}
                    {n.manager_name ? ` · ${n.manager_name}` : ""}
                    {n.stop_check_ins
                      ? " · stopped check-ins"
                      : n.next_check_in_date
                        ? ` · re-check ${fmtDate(n.next_check_in_date)}`
                        : ""}
                  </Typography>
                  {n.note && (
                    <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#374151", whiteSpace: "pre-wrap" }}>
                      {n.note}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <CheckInModal
        open={checkInOpen}
        onClose={() => setCheckInOpen(false)}
        chemistUUID={chemistUUID}
        chemistName={chemistName || data.ChemistName}
        nextCheckInDate={data.NextCheckInDate}
        stopCheckIns={data.StopCheckIns}
        lastCheckInDate={data.LastCheckInDate}
        onSaved={load}
      />
    </Box>
  );
};

export default ChemistAccountHealthTab;
